/**
 * Avatar Column Helpers
 * Contains avatar-related cell components and helpers for table columns
 */

import { AvatarDetailsButton } from "@/components/ui/avatar-details-button";
import type { Group, Label } from "@/types";
import * as LucideIcons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useImageUrl } from "@/hooks/useImage";
import { isDirectImageUrl, isImageId } from "@/utils/imageUtils";

// Helper function to get icon component safely
export function getIconComponent(iconName: string): LucideIcon | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (LucideIcons as any)[iconName] || null;
}

// GroupAvatarCell component for better separation of concerns
export interface GroupAvatarCellProps {
  group: Group;
  onAvatarClick?: (group: Group) => void;
}

export function GroupAvatarCell({ group, onAvatarClick }: GroupAvatarCellProps) {
  const IconComponent = group.icon ? getIconComponent(group.icon) : null;
  const color = group.color || '#3B82F6';
  
  // Handle empty strings by treating them as null/undefined
  const avatarUrl = group.avatarUrl?.trim() || undefined;
  
  // Check if avatarUrl is an image ID that needs resolution
  const needsImageResolution = isImageId(avatarUrl);
  
  // Use the useImageUrl hook to resolve image ID to URL if needed
  const resolvedImageUrl = useImageUrl(needsImageResolution ? avatarUrl : null);
  
  // Determine the final avatar URL to use
  let finalAvatarUrl: string | undefined;
  if (needsImageResolution) {
    finalAvatarUrl = resolvedImageUrl || undefined;
  } else if (isDirectImageUrl(avatarUrl)) {
    finalAvatarUrl = avatarUrl;
  } else {
    finalAvatarUrl = undefined; // Invalid or empty value
  }
  
  // If avatar URL exists, show avatar with initials fallback
  // Otherwise, show icon if available
  if (finalAvatarUrl) {
    return (
      <AvatarDetailsButton
        firstName={group.name || ""}
        lastName=""
        avatarUrl={finalAvatarUrl}
        onClick={() => onAvatarClick?.(group)}
      />
    );
  } else if (IconComponent) {
    return (
      <AvatarDetailsButton
        firstName={group.name || ""}
        lastName=""
        icon={<IconComponent className="h-4 w-4 text-white" aria-hidden="true" />}
        iconBgColor={color}
        onClick={() => onAvatarClick?.(group)}
      />
    );
  } else {
    // No avatar or icon, use initials
    return (
      <AvatarDetailsButton
        firstName={group.name || ""}
        lastName=""
        onClick={() => onAvatarClick?.(group)}
      />
    );
  }
}

// LabelAvatarCell component for consistent label avatar display
export interface LabelAvatarCellProps {
  label: Label;
  onAvatarClick?: (label: Label) => void;
}

export function LabelAvatarCell({ label, onAvatarClick }: LabelAvatarCellProps) {
  const IconComponent = label.icon ? getIconComponent(label.icon) || LucideIcons.Tag : LucideIcons.Tag;
  const color = label.color || '#94A3B8';

  return (
    <AvatarDetailsButton
      firstName={label.label || ""}
      lastName=""
      icon={<IconComponent className="h-4 w-4 text-white" aria-hidden="true" />}
      iconBgColor={color}
      onClick={() => onAvatarClick?.(label)}
    />
  );
}

// Document avatar cell
import type { Document } from "@/types/document";

export interface DocumentAvatarCellProps {
  document: Document;
  onAvatarClick?: (document: Document) => void;
}

export function DocumentAvatarCell({ document, onAvatarClick }: DocumentAvatarCellProps) {
  return (
    <AvatarDetailsButton
      firstName={document.name || ""}
      lastName=""
      icon={<LucideIcons.FileText className="h-4 w-4" aria-hidden="true" />}
      iconClassName="bg-primary text-primary-foreground"
      onClick={() => onAvatarClick?.(document)}
    />
  );
}

// Course types for avatar cells
import type { Course, CoursePlan, Attendance } from "@/types/course";

// CourseAvatarCell component for course catalog
export interface CourseAvatarCellProps {
  course: Course;
  onAvatarClick?: (course: Course) => void;
}

export function CourseAvatarCell({ course, onAvatarClick }: CourseAvatarCellProps) {
  return (
    <AvatarDetailsButton
      firstName={course.name || ""}
      lastName=""
      icon={<LucideIcons.GraduationCap className="h-4 w-4" aria-hidden="true" />}
      iconClassName="bg-[hsl(var(--chart-6))] text-white"
      onClick={() => onAvatarClick?.(course)}
    />
  );
}

// CoursePlanAvatarCell component for course plans
export interface CoursePlanAvatarCellProps {
  plan: CoursePlan;
  onAvatarClick?: (plan: CoursePlan) => void;
}

export function CoursePlanAvatarCell({ plan, onAvatarClick }: CoursePlanAvatarCellProps) {
  return (
    <AvatarDetailsButton
      firstName={plan.course_name || ""}
      lastName=""
      icon={<LucideIcons.Calendar className="h-4 w-4" aria-hidden="true" />}
      iconClassName="bg-info text-info-foreground"
      onClick={() => onAvatarClick?.(plan)}
    />
  );
}

// AttendanceAvatarCell component for attendance records
export interface AttendanceAvatarCellProps {
  attendance: Attendance;
  onAvatarClick?: (attendance: Attendance) => void;
}

export function AttendanceAvatarCell({ attendance, onAvatarClick }: AttendanceAvatarCellProps) {
  // Handle empty strings by treating them as null/undefined
  const avatarUrl = attendance.child_avatar_url?.trim() || undefined;

  // Check if avatarUrl is an image ID that needs resolution
  const needsImageResolution = isImageId(avatarUrl);

  // Use the useImageUrl hook to resolve image ID to URL if needed
  const resolvedImageUrl = useImageUrl(needsImageResolution ? avatarUrl : null);

  // Determine the final avatar URL to use
  let finalAvatarUrl: string | undefined;
  if (needsImageResolution) {
    finalAvatarUrl = resolvedImageUrl || undefined;
  } else if (isDirectImageUrl(avatarUrl)) {
    finalAvatarUrl = avatarUrl;
  } else {
    finalAvatarUrl = undefined; // Invalid or empty value
  }

  // Show child avatar with initials fallback
  return (
    <AvatarDetailsButton
      firstName={attendance.child_firstname || ""}
      lastName={attendance.child_lastname || ""}
      avatarUrl={finalAvatarUrl}
      onClick={() => onAvatarClick?.(attendance)}
    />
  );
}